import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Optional } from 'utility-types';

import { getBasketSize, getSales, PerformanceTotal } from 'apis/merchant-gateway/insights';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';
import { formatGrowthRate, getGrowthRateForFields, getGrowthTrend } from 'modules/insights-v2/utils';

import MetricBlock from './MetricBlock';
import { OrderType } from '../../types';
import WoltPlusLogo from '../WoltPlusLogo';
import { Legend as LegendType, Legends } from '../charts/types';

const Root = styled.div`
  display: flex;
  justify-content: space-between;
  padding-block: 16px;
  padding-inline: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    padding: 0;
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  gap: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 32px;
    padding: 24px;
  }
`;

const BreakDownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    padding: 24px;
    align-items: flex-start;
  }
`;
const BreakDownTitle = styled(T.Body3)`
  align-self: flex-end;
  color: ${cbSemanticCssVars.textSecondary};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-self: flex-start;
  }
`;
const LegendWrapper = styled.div`
  display: flex;
  gap: 24px;
`;
const Legend = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const LegendSquare = styled.div<{ color: string }>`
  background-color: ${({ color }) => color};
  border-radius: 4px;
  height: 12px;
  width: 12px;
`;

const StyledWoltPlusLogo = styled(WoltPlusLogo)`
  fill: ${cbSemanticCssVars.textPrimary};
  height: 10px;
  width: 36px;
`;

const WoltPlusSalesWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;
`;

type Props = {
  totalForSelectedDateRange: PerformanceTotal;
  totalForPreviousDateRange?: PerformanceTotal | null;
  currency: Currency;
  legends: Optional<Legends<OrderType>, OrderType.WoltPlus>;
  isVATEnabled: boolean;
};

const TotalPerformance = ({
  totalForSelectedDateRange,
  totalForPreviousDateRange,
  currency,
  legends,
  isVATEnabled,
}: Props) => {
  const { formatMessage, formatNumber, locale } = useIntl();
  const growthRates =
    totalForPreviousDateRange &&
    getGrowthRateForFields(totalForSelectedDateRange, totalForPreviousDateRange, [
      'salesNoVat',
      'salesWithVat',
      'orders',
      'avgBasketSizeNoVat',
      'avgBasketSizeWithVat',
      'avgBasketSizeItems',
    ]);

  return (
    <Root>
      <TotalWrapper>
        <MetricBlock
          title={formatMessage({ id: 'insights.performance.total-sales' })}
          tooltip={formatMessage({ id: 'insights.total-sales.tooltip' })}
          metrics={formatNumber(getSales(totalForSelectedDateRange, isVATEnabled), { currency, style: 'currency' })}
          growth={formatGrowthRate(getSales(growthRates, isVATEnabled), locale)}
          growthTrend={getGrowthTrend(getSales(growthRates, isVATEnabled))}
        />
        <MetricBlock
          title={formatMessage({ id: 'insights.performance.total-orders' })}
          tooltip={formatMessage({ id: 'insights.total-orders.tooltip' })}
          metrics={totalForSelectedDateRange.orders}
          growth={formatGrowthRate(growthRates?.orders, locale)}
          growthTrend={getGrowthTrend(growthRates?.orders)}
        />
        <MetricBlock
          title={formatMessage({ id: 'insights.performance.average-basket-size' })}
          tooltip={formatMessage({ id: 'insights.average-basket-size.tooltip' })}
          metrics={formatNumber(getBasketSize(totalForSelectedDateRange, isVATEnabled), {
            currency,
            style: 'currency',
          })}
          growth={formatGrowthRate(getBasketSize(growthRates, isVATEnabled), locale)}
          growthTrend={getGrowthTrend(getBasketSize(growthRates, isVATEnabled))}
        />
        <MetricBlock
          title={formatMessage({ id: 'insights.performance.average-basket-size-items' })}
          tooltip={formatMessage({ id: 'insights.average-basket-size-items.tooltip' })}
          metrics={totalForSelectedDateRange.avgBasketSizeItems}
          growth={formatGrowthRate(growthRates?.avgBasketSizeItems, locale)}
          growthTrend={getGrowthTrend(growthRates?.avgBasketSizeItems)}
        />
      </TotalWrapper>
      <BreakDownWrapper>
        <BreakDownTitle>
          <FormattedMessage id="insights.breakdown-by-day" />
        </BreakDownTitle>
        <LegendWrapper>
          {Object.entries(legends).map(([key, value]: [OrderType.Regular | OrderType.WoltPlus, LegendType]) => (
            <Legend key={key}>
              <LegendSquare color={value.color} />
              <WoltPlusSalesWrapper>
                {key === OrderType.WoltPlus && <StyledWoltPlusLogo />}
                <T.Body3>
                  {key === OrderType.Regular ? (
                    <FormattedMessage id="insights.performance.regular-sales" />
                  ) : (
                    <FormattedMessage id="insights.performance.woltplus-sales" />
                  )}
                </T.Body3>
              </WoltPlusSalesWrapper>
            </Legend>
          ))}
        </LegendWrapper>
      </BreakDownWrapper>
    </Root>
  );
};

export default TotalPerformance;
